import { useState } from 'react';
import { filterGenres } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/** Un genere come lo mostra l'elenco: il nome e quanti titoli ci sono dentro. */
export interface PhoneGenre {
  readonly id: string;
  readonly name: string;
  readonly count: number;
}

/**
 * L'elenco completo dei generi, sul telefono, **con il campo per filtrare**.
 *
 * ## Il campo esiste solo qui
 *
 * Quaranta generi su 390 px sono quattro schermate di scorrimento col pollice; scrivere tre lettere
 * è immediato perché c'è una tastiera vera. Sul televisore lo stesso campo sarebbe una punizione —
 * si scrive con le frecce, e tre lettere costano venti pressioni (`04-SCHERMATE-MOBILE.md` §5).
 *
 * ## Il filtro è nel dominio
 *
 * `filterGenres` decide cosa resta: dentro il nome e non dall'inizio, accenti e maiuscole
 * indifferenti. Qui c'è solo il campo e l'elenco — perché quella regola si prova senza montare
 * niente, e il suo sintomo quando sbaglia è sempre lo stesso: un elenco vuoto, che si legge come un
 * catalogo povero.
 *
 * ## Quando non resta niente lo si dice, e si può tornare indietro
 *
 * Un elenco vuoto senza una parola sopra è indistinguibile da un catalogo senza generi. Qui compare
 * una riga che nomina **cosa si è cercato** — e il campo resta pieno, così si corregge invece di
 * riscrivere da capo.
 */
export function PhoneGenresScreen({
  genres,
  onPick,
}: {
  readonly genres: ReadonlyArray<PhoneGenre>;
  readonly onPick: (genreId: string) => void;
}) {
  const translator = useTranslator();
  const [query, setQuery] = useState('');
  const visibili = filterGenres(genres, query);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '8px 0 28px' }}>
      <h1 style={{ margin: 0, padding: '0 20px', font: `800 28px ${FONT_STACK}`, letterSpacing: '-.02em' }}>
        {translator.t('phone.genres.title')}
      </h1>

      <div style={{ padding: '0 20px' }}>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translator.t('phone.genres.filter')}
          aria-label={translator.t('phone.genres.filter')}
          /*
            `type="search"` e non `text`: su iOS e Android porta la crocetta per svuotare e la
            tastiera giusta. Senza, per cancellare una ricerca sbagliata si tengono premuti i tasti.
          */
          style={{
            width: '100%',
            height: 44,
            boxSizing: 'border-box',
            padding: '0 14px',
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            background: colors.surface,
            color: colors.text,
            font: `500 15px ${FONT_STACK}`,
          }}
        />
      </div>

      {visibili.length === 0 ? (
        /*
          Si nomina **cosa si è cercato**. «Nessun risultato» da solo lascia in dubbio fra «questo
          catalogo non ha quel genere» e «ho scritto male», e la seconda è di gran lunga la più
          probabile su una tastiera di telefono.
        */
        <p style={{ margin: 0, padding: '20px', font: `500 14px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('phone.genres.none', { query: query.trim() })}
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
          {visibili.map((genre) => (
            <li key={genre.id}>
              <button
                type="button"
                onClick={() => onPick(genre.id)}
                style={{
                  width: '100%',
                  minHeight: 52,
                  border: 0,
                  borderBottom: `1px solid ${colors.borderFaint}`,
                  background: 'none',
                  color: colors.text,
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: '0 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  font: `600 15px ${FONT_STACK}`,
                }}
              >
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {genre.name}
                </span>
                {/*
                  Il conteggio: distingue un genere con tre titoli da uno con duecento, che è la
                  differenza fra aprirlo e non aprirlo. In grigio perché è un'informazione di
                  contorno, non il nome.
                */}
                <span style={{ flex: 'none', font: `500 13px ${FONT_STACK}`, color: colors.textDim }}>
                  {genre.count}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
